import React, { useState } from 'react';
import { ExpandableCard } from '@/components/shared/ExpandableCard';
import { Button } from '@/components/shared';

interface SeniorPathContentProps {
    onGetStarted: () => void;
    className?: string;
}

export const SeniorPathContent: React.FC<SeniorPathContentProps> = ({
    onGetStarted,
    className = ''
}) => {
    // State to track which service cards are expanded
    const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());

    // State to track "Why pay?" section expansion
    const [isWhyPayExpanded, setIsWhyPayExpanded] = useState(false);

    // Function to toggle expansion of a service card
    const toggleServiceExpansion = (serviceId: string) => {
        const newExpanded = new Set(expandedServices);
        if (newExpanded.has(serviceId)) {
            newExpanded.delete(serviceId);
        } else {
            newExpanded.add(serviceId);
        }
        setExpandedServices(newExpanded);
    };

    const services = [
        {
            id: 'transportation',
            title: 'Transportation & Errands',
            description: 'Safe, reliable help getting where you need to go and running essential errands',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
            ),
            examples: ['Medical appointments and pharmacy visits', 'Grocery shopping and meal pickup', 'Banking and post office trips', 'Social outings and family visits', 'Religious services and community events', 'General errands and returns']
        },
        {
            id: 'home-care',
            title: 'Home Care & Maintenance',
            description: 'Help maintaining a clean, safe, and comfortable home environment',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
            ),
            examples: ['Light housekeeping and organizing', 'Changing light bulbs and batteries', 'Basic home repairs (loose hinges, wobbly furniture)', 'Seasonal decorating and storage', 'Laundry and linen changes', 'Indoor plant care']
        },
        {
            id: 'outdoor-seasonal',
            title: 'Outdoor & Seasonal Help',
            description: 'Assistance with yard work and seasonal outdoor tasks throughout the year',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            ),
            examples: ['Lawn mowing and basic yard care', 'Garden maintenance and watering', 'Leaf raking and debris cleanup', 'Snow removal from walkways', 'Outdoor decoration setup', 'Patio and deck tidying']
        },
        {
            id: 'companionship',
            title: 'Companionship & Activities',
            description: 'Connections through conversation, shared activities, and social engagement',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
            examples: ['Friendly conversation and active listening', 'Reading together or storytelling', 'Card games, puzzles, and board games', 'Hobby sharing and craft projects', 'Accompanying to social events', 'Simply being present during daily activities']
        },
        {
            id: 'technology',
            title: 'Technology & Communication',
            description: 'Patient support navigating phones, computers, and staying connected digitally',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            ),
            examples: ['Video call setup with family', 'Smartphone and tablet assistance', 'Email and text messaging help', 'Online shopping and banking guidance', 'Social media basics', 'Printer and device troubleshooting']
        },
        {
            id: 'wellness',
            title: 'Wellbeing & Activities',
            description: 'Gentle support for physical activity, nutrition, and daily wellness routines',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            ),
            examples: ['Walking companion for exercise', 'Gentle stretching and movement', 'Meal planning and preparation help', 'Medication reminders', 'Wellness appointment companionship', 'Daily routine support']
        }
    ];

    const steps = [
        {
            number: '1',
            title: 'Sign Up & Tell Us Your Needs',
            description: 'Create your profile and let us know what kind of help you need',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            )
        },
        {
            number: '2',
            title: 'Get Matched with Helpers',
            description: 'We connect you with background-checked volunteers and professionals nearby',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            )
        },
        {
            number: '3',
            title: 'Receive Reliable Help',
            description: 'Enjoy consistent, caring support that helps you stay independent',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            )
        }
    ];

    return (
        <div className={className}>
            {/* Services Available Section */}
            <section className="pt-16 pb-8 bg-blue-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            The Support You Need, When You Need It
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our caring volunteers and trusted professionals are ready to help with daily tasks that matter most to you
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto items-start">
                        {services.map((service) => (
                            <ExpandableCard
                                key={service.id}
                                title={service.title}
                                description={service.description}
                                icon={service.icon}
                                examples={service.examples}
                                colorScheme="blue"
                                isExpanded={expandedServices.has(service.id)}
                                onToggle={() => toggleServiceExpansion(service.id)}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Getting Started Section */}
            <section className="pt-8 pb-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Getting Started is Simple
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            We've designed BiBi to be easy to use, even if technology isn't your strong suit
                        </p>
                    </div>

                    <div className="max-w-6xl mx-auto">
                        <div className="grid md:grid-cols-3 gap-8">
                            {steps.map((step) => (
                                <div key={step.number} className="text-center">
                                    {/* Step Circle with Icon */}
                                    <div className="relative mb-6">
                                        <div className="w-20 h-20 bg-gradient-to-br from-bibi-blue to-bibi-blue/80 rounded-full flex flex-col items-center justify-center mx-auto mb-4 shadow-lg">
                                            <span className="text-white text-sm font-bold mb-1">{step.number}</span>
                                            <div className="text-white">
                                                {step.icon}
                                            </div>
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                                </div>
                            ))}
                        </div>

                        {/* Additional Help Options */}
                        <div className="mt-12">
                            <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                                {/* Technology Reassurance */}
                                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 text-center">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Don't worry about technology</h4>
                                    <p className="text-gray-600 leading-relaxed">
                                        You can call us directly to request help. Our friendly staff will handle all the matching and scheduling for you.
                                        Technology should make life easier, not harder.
                                    </p>
                                </div>

                                {/* Family Help Option */}
                                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 text-center">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-3">With Family Help</h4>
                                    <p className="text-gray-600 leading-relaxed">
                                        Family members can create accounts and manage requests on your behalf. Stay in control while getting support from loved ones.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Membership Tiers Pricing Section */}
            <section className="py-4 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Membership Plans
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our unique volunteer + professional model keeps costs low while ensuring reliability.
                            Start free and upgrade when you need more support.
                        </p>
                    </div>

                    <div className="max-w-7xl mx-auto">
                        {/* Three-Tier Pricing Cards */}
                        <div className="grid lg:grid-cols-3 gap-8 mb-12">
                            {/* Tier 1: Basic Access */}
                            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 relative">
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Basic Access</h3>
                                    <p className="text-gray-600 mb-4">No monthly fees</p>
                                    <div className="text-5xl font-bold text-sage-green mb-2">Free</div>
                                    <p className="text-sm text-gray-500">Perfect for occasional needs</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-sage-green mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span><strong>Up to 2 requests</strong> per month</span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-sage-green mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Connect with available community volunteers and professionals
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-sage-green mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Standard matching for all tasks
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-sage-green mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Access to BiBi community news & resources
                                    </li>
                                </ul>
                            </div>

                            {/* Tier 2: Community Plus - Most Popular */}
                            <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-bibi-blue relative transform lg:scale-105">
                                {/* Most Popular Badge */}
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                    <div className="bg-bibi-blue text-white px-6 py-2 rounded-full text-sm font-bold">
                                        Most Popular
                                    </div>
                                </div>

                                <div className="text-center mb-8 mt-4">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Community Plus</h3>
                                    <p className="text-gray-600 mb-4">Our most popular plan for regular support</p>
                                    <div className="text-5xl font-bold text-bibi-blue mb-1">$27</div>
                                    <div className="text-gray-500">/month</div>
                                    <p className="text-sm text-gray-500 mt-2">Best value for regular needs</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-bibi-blue mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span><strong>Everything in Basic Access, plus:</strong></span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-bibi-blue mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span><strong>Unlimited requests</strong></span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-bibi-blue mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Family Caregiver Portal access
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-bibi-blue mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Monthly family care coordination call
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-bibi-blue mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Preferred helper designation
                                    </li>
                                </ul>
                            </div>

                            {/* Tier 3: Complete Connect */}
                            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-purple-200 relative">
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Complete Connect</h3>
                                    <p className="text-gray-600 mb-4">Our highest level of access & support</p>
                                    <div className="text-5xl font-bold text-purple-600 mb-1">$49</div>
                                    <div className="text-gray-500">/month</div>
                                    <p className="text-sm text-gray-500 mt-2">Maximum support & priority</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span><strong>Everything in Community Plus, plus:</strong></span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span><strong>Highest priority access</strong> for all requests</span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span><strong>Same-day service requests</strong> (before 10 AM)</span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Dedicated support line
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Weekly family care coordination call
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Service Cost Explanation */}
                        <div className="bg-gray-50 rounded-xl p-6 max-w-6xl mx-auto">
                            <div className="grid md:grid-cols-2 gap-8">
                                {/* Volunteer Services */}
                                <div className="text-center">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Volunteer Services</h4>
                                    <div className="text-3xl font-bold text-sage-green mb-2">No Additional Cost</div>
                                    <p className="text-gray-600 text-sm">
                                        Community volunteers provide help at no hourly cost.
                                    </p>
                                </div>

                                {/* Professional Services */}
                                <div className="text-center">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Professional Services</h4>
                                    <div className="text-3xl font-bold text-bibi-blue mb-2">Hourly Rate</div>
                                    <p className="text-gray-600 text-sm">
                                        When volunteers aren't available or for specialized tasks.
                                        Transparent hourly rates, confirmed before booking.
                                    </p>
                                </div>
                            </div>

                            {/* Disclaimer */}
                            <div className="border-t border-gray-200 mt-6 pt-6 text-center">
                                <p className="text-sm text-gray-600">
                                    *Your monthly credits are automatically applied to any professional services.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Pay When Volunteers Are Involved Section */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                Why Pay for the Service?
                            </h2>
                            <p className="text-lg text-gray-700 mb-6">
                                You're paying for the platform that makes community help possible - not for the volunteers' time. Your membership funds the technology, safety systems, and coordination that connects you with caring neighbors.
                            </p>

                            <button
                                onClick={() => setIsWhyPayExpanded(!isWhyPayExpanded)}
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-bibi-blue bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bibi-blue transition-colors duration-200 shadow-sm border-gray-200"
                                aria-expanded={isWhyPayExpanded}
                                aria-controls="why-pay-details"
                            >
                                Learn more about the value
                                <svg
                                    className={`ml-2 w-5 h-5 transform transition-transform duration-200 ${isWhyPayExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {/* Expandable Content */}
                        <div
                            id="why-pay-details"
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${isWhyPayExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                                }`}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                                {/* What Your Membership Actually Covers */}
                                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                                    <div className="flex items-center mb-4">
                                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900">Reliable Infrastructure</h3>
                                    </div>
                                    <p className="text-gray-700">
                                        Our technology platform handles all the behind-the-scenes work - finding helpers, coordinating schedules, sending reminders, and managing communications.
                                    </p>
                                </div>

                                {/* Safety & Security */}
                                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                                    <div className="flex items-center mb-4">
                                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900">Safety & Security Systems</h3>
                                    </div>
                                    <p className="text-gray-700">
                                        Background checks for every helper, liability insurance coverage, support hotline, and secure payment processing for professional services.
                                    </p>
                                </div>

                                {/* Coordination & Management */}
                                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                                    <div className="flex items-center mb-4">
                                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                                            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900">Expert Coordination</h3>
                                    </div>
                                    <p className="text-gray-700">
                                        We handle the logistics that make community help work: vetting helpers, matching you based on your needs and location, check-ins to ensure everything goes smoothly.
                                    </p>
                                </div>
                            </div>

                            {/* Bottom Line Summary */}
                            <div className="mt-8 bg-bibi-blue-light bg-opacity-10 rounded-lg p-6 border border-bibi-blue-light border-opacity-20">
                                <div className="text-center text-lg font-medium text-gray-900">
                                    <strong>The Bottom Line:</strong> Volunteers give their time freely. You're investing in the platform that makes their generosity reliable, safe, and accessible to you.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-bibi-blue text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                        Ready to Maintain Your Independence?
                    </h2>
                    <p className="text-xl text-bibi-blue-light mb-8 max-w-2xl mx-auto">
                        Join our early access list to be among the first seniors in Wichita to experience BiBi
                    </p>
                    <Button
                        variant="secondary"
                        size="lg"
                        onClick={onGetStarted}
                        className="bg-sage-green hover:bg-sage-green/90 focus:ring-sage-green/50"
                    >
                        Join Early Access List
                    </Button>
                    <p className="text-sm text-bibi-blue-light mt-4">
                        No commitment required • Launch early 2026
                    </p>
                </div>
            </section>
        </div>
    );
};
